import { Box, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { JSX } from "react";

import { PageLoader } from "@/components/ui/page-loader";

import { BrainSection } from "../brain/BrainSection.js";
import { salesApi } from "../client.js";
import { AgentModeToggle } from "../components/AgentModeToggle.js";
import { TwoAgentStrip } from "../components/AgentTrackPanels.js";
import { EconomicsStrip } from "../components/EconomicsStrip.js";
import { QualityStrip } from "../components/QualityStrip.js";
import { SalesErrorState } from "../components/SalesEmptyState.js";
import { devLog } from "../devLog.js";
import { ActiveDirectives } from "./ActiveDirectives.js";
import { AgentHero } from "./AgentHero.js";
import { HarvesterStatus } from "./HarvesterStatus.js";
import { LeadFocusPanel } from "./LeadFocusPanel.js";
import { MissionBanner } from "./MissionBanner.js";

/**
 * Status tab — "is the agent working, what's the plan, what does it cost?"
 *
 * Born from the old Today tab (2026-06-16): the leads-of-today list moved to
 * a **"Today" filter on the Leads tab**, and the agent-health + spend panels
 * that used to ride Today's right rail moved here so the operator's home is
 * now the Leads list (the default landing). This page answers three glance
 * questions:
 *
 *   1. Is the agent working?      (AgentHero — status line + tone dot)
 *   2. What's the plan?           (MissionBanner — strategist memo TL;DR)
 *   3. What does it cost / how    (EconomicsStrip — today's LLM spend +
 *      good are the leads?         daily-budget gauge; QualityStrip — the
 *                                  LLM-judge's grounding/fit + verdict split)
 *
 *   + the agent's active standing directives (ActiveDirectives), with the
 *     archive control.
 *   4. What is it doing this second? (BrainSection — the ex-`/sales/brain`
 *      tab, folded in at the BOTTOM 2026-08-07 so the page reads top-to-
 *      bottom: who's working → what it costs → how far through the target
 *      map → its live transcript. `/sales/brain` now redirects here.)
 *
 * The Steer + Pause/Resume controls live in the SalesLayout header, so the
 * operator can drive the agent from any Sales tab. Polls every 30 s; never
 * collapses to a generic 500 — SalesErrorState renders a typed empty state
 * for every backend code.
 */
export function StatusPage(): JSX.Element {
  useEffect(() => devLog("status/mount"), []);

  const today = useQuery({
    queryKey: ["sales", "today"],
    queryFn: salesApi.getToday,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  // The Status page is MODE-AWARE: in contacts mode it shows the harvester's
  // status; in leads mode the lead agent's; in both, both.
  const ctl = useQuery({
    queryKey: ["sales", "agent-control"],
    queryFn: salesApi.getAgentControl,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
  const mode = ctl.data?.mode ?? "leads";
  const showContacts = mode === "contacts" || mode === "both";
  const showLeads = mode === "leads" || mode === "both";

  useEffect(() => {
    if (today.data) {
      devLog("status/render", {
        status_line: today.data.status_line,
        current_mission: today.data.current_mission_label,
        has_strategy_tldr: Boolean(today.data.weekly_mission_paragraph),
      });
    }
  }, [today.data]);

  if (ctl.isLoading && !ctl.data) {
    return <PageLoader />;
  }

  // The lead-agent status block — only rendered when the LEAD track is active.
  const leadSection = (() => {
    if (!showLeads) return null;
    if (today.isLoading) return <PageLoader />;
    if (today.isError) return <SalesErrorState error={today.error} surface="status" />;
    const data = today.data;
    if (!data) return <SalesErrorState error={new Error("Empty payload")} surface="status" />;
    return (
      <>
        <Box
          sx={{
            display: "grid",
            gap: { xs: 2, lg: 2.5 },
            alignItems: "stretch",
            gridTemplateColumns: { lg: "minmax(0, 3fr) minmax(0, 2fr)" },
          }}
        >
          <AgentHero
            statusLine={data.status_line}
            isPaused={data.is_paused ?? false}
            currentMissionLabel={data.current_mission_label}
            lastReflection={data.last_reflection}
          />
          <MissionBanner
            tldr={data.weekly_mission_paragraph}
            writtenAt={data.weekly_mission_written_at ?? null}
            expiresAt={data.weekly_mission_expires_at ?? null}
          />
        </Box>
        <LeadFocusPanel />
        <QualityStrip />
        <ActiveDirectives />
      </>
    );
  })();

  return (
    // FULL WIDTH — like every other Sales page. This grid used to be capped at
    // `maxWidth: 1100` from 2026-06-16, when Status was a narrow three-card
    // glance. Folding the Brain transcript in below (2026-08-07) made that cap
    // wrong: the chat + mission-backlog layout is built to breathe (its `xl`
    // two-column split can't even engage inside 1100px), and the coverage /
    // spend bars read as a stranded column on a wide monitor. Don't re-add it.
    <Box
      data-testid="sales-status-page"
      sx={{ display: "grid", gap: { xs: 2, lg: 2.5 }, width: "100%" }}
    >
      {/* Which AI bot is working — the at-a-glance two-agent state PLUS the
          selector that picks who runs (moved here from the Sales header
          2026-06-21). The toggle is gated on sales:edit (it self-hides for
          read-only viewers, who still see the state below). */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
        >
          Which AI bot is working
        </Typography>
        <AgentModeToggle />
      </Box>
      <TwoAgentStrip />

      {/* Shared daily budget / spend (both tracks draw from it). */}
      <EconomicsStrip />

      {/* CONTACTS track status — pool counts, coverage, live activity. */}
      {showContacts && <HarvesterStatus />}

      {/* LEADS track status — hero, memo, quality, directives. */}
      {leadSection}

      {/* The agent's live consciousness, folded in from the retired Brain tab
          (2026-08-07). It sits LAST — under the coverage bar / directives —
          because it's the "watch it work" surface, not the glance. Mode-aware
          like everything above it. */}
      <Divider sx={{ mt: 1 }} />
      <BrainSection showLeads={showLeads} showContacts={showContacts} />
    </Box>
  );
}
